// @ts-nocheck
import React, { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Ban, Rocket, Trash2, Target } from 'lucide-react';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';

interface User {
	_id: Id<'users'>;
	email: string;
	name: string;
	role?: string;
	isAdmin?: boolean;
	isActive?: boolean;
	walletAddress?: string;
	initialApiKey?: string;
	profileKey?: string;
	profileTitle?: string;
	lastWarningAcknowledgment?: string;
	lastLoginAt?: string;
	createdAt?: string;
	updatedAt?: string;
	tokenIdentifier: string;
}

export function UsersList({
	users,
	requestingUserId,
}: {
	users: User[];
	requestingUserId: Id<'users'>;
}) {
	const { toast } = useToast();
	const { getMemberAuthToken } = useWeb3Auth();
	const updateUserRole = useMutation(api.users.updateRoleAuthorized);
	const banUser = useMutation(api.users.banUserFromAdminAuthorized);
	const deleteUser = useMutation(api.users.deleteUserAuthorized);
	const [userToBan, setUserToBan] = useState<User | null>(null);
	const [isBanning, setIsBanning] = useState(false);
	const [userToDelete, setUserToDelete] = useState<User | null>(null);

	// Oracle Sniper Team mutations
	const addToOracleSniperTeam = useMutation(
		api.oracleSniperTeam.addToOracleSniperTeam,
	);
	const enableTradingAccount = useMutation(
		api.oracleSniperTeam.enableTradingAccount,
	);
	const disableTradingAccount = useMutation(
		api.oracleSniperTeam.disableTradingAccount,
	);
	const deleteTradingAccount = useMutation(
		api.oracleSniperTeam.deleteTradingAccount,
	);
	const teamMembers = useQuery(
		api.oracleSniperTeam.listOracleSniperTeamMembers,
		{ includeInactive: true },
	);

	const [tradingAccountToDelete, setTradingAccountToDelete] = useState<{
		id: Id<'tradingAccounts'>;
		userName: string;
	} | null>(null);

	// Helper to get trading account for a user
	const getTradingAccount = (userId: Id<'users'>) => {
		return teamMembers?.find((member) => member.user?._id === userId)
			?.tradingAccount;
	};

	// Oracle Sniper Team handlers
	const handleAddToTeam = async (user: User) => {
		try {
			await addToOracleSniperTeam({ userId: user._id });
			toast({
				title: '🚀 Added to Oracle Sniper Team',
				description: `${user.name} is now an Oracle Sniper`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '❌ Error',
				description:
					error instanceof Error ? error.message : 'Failed to add to team',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleToggleTradingActive = async (
		tradingAccountId: Id<'tradingAccounts'>,
		currentlyActive: boolean,
	) => {
		try {
			if (currentlyActive) {
				await disableTradingAccount({ tradingAccountId });
				toast({
					title: '⏸️ Trading Disabled',
					description: 'Trading account has been disabled',
					duration: 3000,
				});
			} else {
				await enableTradingAccount({ tradingAccountId });
				toast({
					title: '✅ Trading Enabled',
					description: 'Trading account has been enabled',
					duration: 3000,
				});
			}
		} catch (error) {
			toast({
				title: '❌ Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to toggle trading status',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleDeleteTradingAccount = async () => {
		if (!tradingAccountToDelete) return;
		try {
			await deleteTradingAccount({
				tradingAccountId: tradingAccountToDelete.id,
			});
			toast({
				title: '🗑️ Trading Account Deleted',
				description: 'User removed from Oracle Sniper Team',
				duration: 3000,
			});
			setTradingAccountToDelete(null);
		} catch (error) {
			toast({
				title: '❌ Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to delete trading account',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const isSuperAdmin = (user: User) =>
		user.email === 'reverend@thewatchersociety.org';

	const isBannedUser = (user: User) =>
		String(user.walletAddress || '')
			.trim()
			.toUpperCase() === 'BANNED' ||
		String(user.role || '')
			.trim()
			.toLowerCase() === 'banned';

	const handleRoleChange = async (user: User) => {
		if (isSuperAdmin(user)) {
			toast({
				title: '❌ Action Denied',
				description: 'Cannot modify super admin role',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		const currentRole = user.role || 'user';
		try {
			if (!getMemberAuthToken) throw new Error('MEMBER_SIGNED_AUTH_REQUIRED');
			const newRole = currentRole === 'superadmin' ? 'admin' : 'superadmin';
			await updateUserRole({
				userId: user._id,
				role: newRole,
				memberToken: await getMemberAuthToken(),
			});
			toast({
				title: '✅ Role Updated',
				description: `User role updated to ${newRole}`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Failed to update role',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleDelete = async (user: User) => {
		if (isSuperAdmin(user)) {
			toast({
				title: '❌ Action Denied',
				description: 'Cannot delete super admin account',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		setUserToDelete(user);
	};

	const handleBan = (user: User) => {
		if (isSuperAdmin(user)) {
			toast({
				title: '❌ Action Denied',
				description: 'Cannot ban protected super admin account',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}
		if (isBannedUser(user)) {
			toast({
				title: '❌ Action Denied',
				description: 'User is already banned',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}
		setUserToBan(user);
	};

	const handleBanConfirm = async () => {
		if (!userToBan) return;
		try {
			setIsBanning(true);
			if (!getMemberAuthToken) throw new Error('MEMBER_SIGNED_AUTH_REQUIRED');
			await banUser({
				userId: userToBan._id,
				reason: 'Prometheus super admin ban button',
				memberToken: await getMemberAuthToken(),
			});
			toast({
				title: 'User Banned',
				description: `${userToBan.name || userToBan.email} can no longer sign up or access Watcher/Telehealth areas.`,
				duration: 3000,
			});
			setUserToBan(null);
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Failed to ban user',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsBanning(false);
		}
	};

	const renderUserTable = (users: User[]) => (
		<div className="w-full overflow-x-auto">
			<table className="w-full divide-y divide-gray-800">
				<thead className="bg-gray-900/50">
					<tr>
						<th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
							Name
						</th>
						<th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
							Email
						</th>
						<th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
							Status
						</th>
						<th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
							Role
						</th>
						<th className="px-4 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">
							🎯 Oracle Sniper
						</th>
						<th className="px-4 py-4 text-right text-xs font-bold text-gray-300 uppercase tracking-wider">
							Actions
						</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-800 bg-black/20">
					{users.map((user) => {
						const userBanned = isBannedUser(user);
						const isCurrentUser = String(user._id) === String(requestingUserId);
						return (
							<tr
								key={user._id}
								className="hover:bg-gray-900/30 transition-colors"
							>
								<td className="px-4 py-4 whitespace-nowrap">
									<div className="text-sm font-medium text-gray-100">
										{user.name}
									</div>
								</td>
								<td className="px-4 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-300">{user.email}</div>
								</td>
								<td className="px-4 py-4 whitespace-nowrap">
									<Badge
										variant="outline"
										className={cn(
											'px-2 py-1 text-xs',
											userBanned
												? 'bg-gradient-to-b from-red-400 to-red-600 text-white border-red-400'
												: user.isActive
													? 'bg-gradient-to-b from-emerald-400 to-emerald-600 text-white border-emerald-400'
													: 'bg-gradient-to-b from-red-400 to-red-600 text-white border-red-400',
										)}
									>
										{userBanned
											? 'BANNED'
											: user.isActive
												? '✅ ACTIVE'
												: '❌ INACTIVE'}
									</Badge>
								</td>
								<td className="px-4 py-4 whitespace-nowrap">
									<Badge
										variant="outline"
										className={cn(
											'px-2 py-1 text-xs',
											user.role === 'superadmin'
												? 'bg-gradient-to-b from-purple-400 to-purple-600 text-white border-purple-400'
												: user.role === 'admin'
													? 'bg-gradient-to-b from-orange-400 to-orange-600 text-white border-orange-400'
													: 'bg-gradient-to-b from-blue-400 to-blue-600 text-white border-blue-400',
										)}
									>
										{user.role === 'superadmin'
											? '👑 SUPERADMIN'
											: user.role === 'admin'
												? '⭐ ADMIN'
												: '👤 USER'}
									</Badge>
								</td>
								{/* Oracle Sniper Team Column */}
								<td className="px-4 py-4 whitespace-nowrap">
									{(() => {
										const tradingAccount = getTradingAccount(user._id);
										if (!tradingAccount) {
											// Not a team member - show Add button
											return (
												<Button
													onClick={() => handleAddToTeam(user)}
													size="sm"
													className={cn(
														'bg-gradient-to-r from-purple-500 via-pink-500 to-pink-600',
														'hover:from-purple-600 hover:via-pink-600 hover:to-pink-700',
														'text-white font-bold text-xs',
														'shadow-[0_0_15px_rgba(236,72,153,0.4)]',
														'hover:shadow-[0_0_25px_rgba(236,72,153,0.6)]',
														'transition-all duration-300',
													)}
												>
													<Rocket className="w-3 h-3 mr-1" />
													Add to Team
												</Button>
											);
										}
										// Is a team member - show toggle and delete
										return (
											<div className="flex items-center gap-2">
												{/* Status Badge */}
												<Badge
													className={cn(
														'px-2 py-1 text-xs',
														tradingAccount.isActive
															? 'bg-gradient-to-r from-emerald-500/20 to-green-600/20 text-emerald-400 border border-emerald-500/30'
															: 'bg-gradient-to-r from-amber-500/20 to-orange-600/20 text-amber-400 border border-amber-500/30',
													)}
												>
													<Target className="w-3 h-3 mr-1 inline" />
													{tradingAccount.isActive ? 'Active' : 'Paused'}
												</Badge>
												{/* Enable/Disable Toggle */}
												<Switch
													checked={tradingAccount.isActive}
													onCheckedChange={() =>
														handleToggleTradingActive(
															tradingAccount._id,
															tradingAccount.isActive,
														)
													}
													className={cn(
														'data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-emerald-500 data-[state=checked]:to-green-600',
														'data-[state=unchecked]:bg-gradient-to-r data-[state=unchecked]:from-gray-500 data-[state=unchecked]:to-gray-600',
													)}
												/>
												{/* Delete Trading Account Button */}
												<Button
													onClick={() =>
														setTradingAccountToDelete({
															id: tradingAccount._id,
															userName: user.name,
														})
													}
													variant="ghost"
													size="icon"
													className={cn(
														'h-8 w-8',
														'bg-gradient-to-r from-red-500/10 to-red-700/10',
														'hover:from-red-500 hover:to-red-700',
														'text-red-400 hover:text-white',
														'shadow-[0_0_8px_rgba(239,68,68,0.2)]',
														'hover:shadow-[0_0_15px_rgba(239,68,68,0.5)]',
														'transition-all duration-300',
													)}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										);
									})()}
								</td>
								<td className="px-4 py-4 whitespace-nowrap text-right">
									<div className="flex justify-end gap-2">
										<Button
												onClick={() => handleRoleChange(user)}
												disabled={
													!user.isActive || userBanned || isSuperAdmin(user)
												}
											variant="outline"
											size="sm"
											className={cn(
												'transition-all text-xs',
												user.role === 'superadmin'
													? 'bg-gradient-to-b from-purple-400/10 to-purple-600/10 hover:from-purple-400 hover:to-purple-600 text-purple-400 hover:text-white border-purple-500'
													: 'bg-gradient-to-b from-blue-400/10 to-blue-600/10 hover:from-blue-400 hover:to-blue-600 text-blue-400 hover:text-white border-blue-500',
												!user.isActive && 'opacity-50 cursor-not-allowed',
												userBanned && 'opacity-50 cursor-not-allowed',
												isSuperAdmin(user) && 'opacity-50 cursor-not-allowed',
											)}
										>
											{user.role === 'superadmin'
												? 'Remove Superadmin'
												: 'Make Superadmin'}
										</Button>
										<Button
											onClick={() => handleBan(user)}
											variant="outline"
											size="sm"
											disabled={
												isSuperAdmin(user) || isCurrentUser || userBanned
											}
											className={cn(
												'bg-gradient-to-b from-amber-400/10 to-red-600/10 hover:from-amber-400 hover:to-red-600 text-amber-400 hover:text-white border-amber-500 text-xs',
												(isSuperAdmin(user) || isCurrentUser || userBanned) &&
													'opacity-50 cursor-not-allowed',
											)}
										>
											<Ban className="w-3 h-3 mr-1" />
											Ban
										</Button>
										<Button
											onClick={() => handleDelete(user)}
											variant="outline"
											size="sm"
											disabled={isSuperAdmin(user)}
											className={cn(
												'bg-gradient-to-b from-red-400/10 to-red-600/10 hover:from-red-400 hover:to-red-600 text-red-400 hover:text-white border-red-500 text-xs',
												isSuperAdmin(user) && 'opacity-50 cursor-not-allowed',
											)}
										>
											Delete
										</Button>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);

	return (
		<>
			{renderUserTable(users)}

			<ConfirmDialog
				isOpen={userToBan !== null}
				onClose={() => setUserToBan(null)}
				title="Ban User"
				description={
					<div className="space-y-4">
						<div className="p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner">
							<div className="text-lg text-zinc-600 dark:text-zinc-400 text-center">
								You are about to ban:
								<br />
								<span className="font-semibold text-amber-500">
									{userToBan?.name}
								</span>
								<br />
								<span className="text-sm text-zinc-500">
									{userToBan?.email}
								</span>
							</div>
						</div>
						<p className="text-sm text-zinc-500 text-center">
							This keeps their database rows, sets their wallet to BANNED,
							disables Watcher access, rejects Telehealth access, and revokes
							active Telehealth sessions.
						</p>
					</div>
				}
				onConfirm={handleBanConfirm}
				confirmText={isBanning ? 'Banning...' : 'Ban User'}
				cancelText="Cancel"
				isWarningDialog={true}
				preventClose={isBanning}
			/>

			<ConfirmDialog
				isOpen={userToDelete !== null}
				onClose={() => setUserToDelete(null)}
				title=""
				description={
					<div className="space-y-4">
						<div className="p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner">
							<div className="text-lg text-zinc-600 dark:text-zinc-400 text-center">
								You are about to delete:
								<br />
								<span className="font-semibold text-pink-500">
									{userToDelete?.name}
								</span>
								<br />
								<span className="text-sm text-zinc-500">
									{userToDelete?.email}
								</span>
							</div>
						</div>
					</div>
				}
				onConfirm={async () => {
					if (userToDelete) {
						try {
							if (!getMemberAuthToken) throw new Error('MEMBER_SIGNED_AUTH_REQUIRED');
							await deleteUser({
								userId: userToDelete._id,
								memberToken: await getMemberAuthToken(),
							});
							toast({
								title: '✅ User Deleted',
								description: `User ${userToDelete.name} has been deleted`,
								duration: 3000,
							});
							setUserToDelete(null);
						} catch (error) {
							console.error('Delete user error:', error);
							toast({
								title: 'Error',
								description:
									error instanceof Error
										? error.message
										: 'Failed to delete user',
								variant: 'destructive',
								duration: 3000,
							});
						}
					}
				}}
				isDeleteDialog={true}
			/>

			{/* Delete Trading Account Confirmation */}
			<ConfirmDialog
				isOpen={tradingAccountToDelete !== null}
				onClose={() => setTradingAccountToDelete(null)}
				title=""
				description={
					<div className="space-y-4">
						<div className="p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner">
							<div className="text-lg text-zinc-600 dark:text-zinc-400 text-center">
								<div className="text-2xl mb-2">🎯</div>
								Remove from Oracle Sniper Team:
								<br />
								<span className="font-semibold text-pink-500">
									{tradingAccountToDelete?.userName}
								</span>
								<br />
								<span className="text-sm text-red-400 mt-2 block">
									This will permanently delete their trading account
									configuration.
								</span>
							</div>
						</div>
					</div>
				}
				onConfirm={handleDeleteTradingAccount}
				isDeleteDialog={true}
			/>
		</>
	);
}
